/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

import { Button, useReducedMotion } from '@coursera/cds-core';

import ActionBar from 'bundles/expression-of-interest/components/ActionBar';
import HeaderLogo from 'bundles/page-header/components/HeaderLogo';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/premium-hub';

type HubHeroStickyActionBarProps = {
  // The bar appears once this element has scrolled out of view.
  anchorRef: RefObject<HTMLElement>;
  programName?: string;
};

const HubHeroStickyActionBar = ({ anchorRef, programName }: HubHeroStickyActionBarProps) => {
  const [isScrollPastAnchor, setIsScrollPastAnchor] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return undefined;
    }

    // The anchor is past once it is out of view and sits above the viewport (not below it).
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => setIsScrollPastAnchor(!entry.isIntersecting && entry.boundingClientRect.bottom < 0));
    });
    observer.observe(anchor);
    return () => observer.disconnect();
  }, [anchorRef]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });

  const actionBarData = {
    isFixed: isScrollPastAnchor,
    partnerName: _t('Coursera Logo'),
    programName,
    partnerMonotoneLogo: (
      <HeaderLogo
        disableUserInteraction={false}
        enableCourseraLogoOnly={true}
        isOpened={false}
        displaySwitcher={false}
        logoWrapper="div"
      />
    ),
  };

  return (
    <ActionBar actionBarData={actionBarData}>
      <div className="eoi-buttons-container">
        <Button
          variant="primary"
          trackingName="hub_hero_sticky_action_bar_scroll_to_top"
          component={TrackedButton}
          withVisibilityTracking={false}
          requireFullyVisible={false}
          onClick={scrollToTop}
          fullWidth
        >
          {_t('Get Started')}
        </Button>
      </div>
    </ActionBar>
  );
};

export default HubHeroStickyActionBar;
